(() => {
  if (customElements.get('fh-cookie-bar')) return;

  const STORAGE_KEY = 'fh_cookie_consent';

  class FhCookieBar extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('.fh-cookie__bar');
      this.toggles = Array.from(this.querySelectorAll('[data-fh-cookie-consent]'));
      this.customizeButton = this.querySelector('[data-fh-cookie-action="customize"]');

      this.addEventListener('click', this.handleClick);

      if (this.dataset.designMode === 'true') {
        this.reveal();
        document.addEventListener('shopify:section:select', this.handleEditorSelect);
        return;
      }

      /* Un choix déjà enregistré dans ce navigateur : on ne redemande pas. */
      if (this.storedConsent()) {
        this.trace('choix-deja-memorise');
        return;
      }

      /*
        Garde-fou : si l'API Customer Privacy ne répond pas dans le délai
        (chargement bloqué, réseau lent), on affiche quand même — un visiteur
        sans choix enregistré doit voir le bandeau.
      */
      let decided = false;
      const decide = (reason, show) => {
        if (decided) return;
        decided = true;
        this.trace(reason);
        if (show) this.reveal();
      };

      window.setTimeout(() => decide('api-sans-reponse', true), 1500);

      this.loadPrivacyApi()
        .then((api) => {
          this.api = api;
          decide(this.shouldShow() ? 'api-ok-afficher' : 'api-ok-consentement-deja-donne', this.shouldShow());
        })
        .catch(() => {
          decide('api-indisponible', true);
        });
    }

    /* Trace lisible dans le DOM (data-fh-debug) pour diagnostiquer sans console. */
    trace(reason) {
      this.dataset.fhDebug = reason;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:section:select', this.handleEditorSelect);
    }

    /* Dans l'éditeur de thème, re-afficher la barre quand la section est sélectionnée. */
    handleEditorSelect = (event) => {
      if (event.target.contains(this)) this.reveal();
    };

    /* L'API Customer Privacy est chargée à la demande par Shopify. */
    loadPrivacyApi() {
      return new Promise((resolve, reject) => {
        if (window.Shopify?.customerPrivacy) {
          resolve(window.Shopify.customerPrivacy);
          return;
        }

        if (typeof window.Shopify?.loadFeatures !== 'function') {
          reject(new Error('Shopify.loadFeatures unavailable'));
          return;
        }

        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            if (error || !window.Shopify.customerPrivacy) {
              reject(error || new Error('customerPrivacy unavailable'));
              return;
            }
            resolve(window.Shopify.customerPrivacy);
          }
        );
      });
    }

    storedConsent() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return null;
      }
    }

    rememberConsent() {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch (error) {
        /* mode navigation privée : on ignore */
      }
    }

    /*
      Le bandeau s'affiche tant que le visiteur n'a pas fait de choix.

      On ne dépend plus uniquement de shouldShowBanner() : cette fonction ne
      renvoie true que si la boutique impose le consentement pour la région
      du visiteur (réglage « Confidentialité des clients » dans l'admin).
      Quand ce réglage est absent ou limité, elle renvoie false et le bandeau
      restait invisible. Désormais : un consentement déjà enregistré chez
      Shopify cache le bandeau, tout le reste l'affiche.
    */
    shouldShow() {
      try {
        if (typeof this.api?.shouldShowBanner === 'function' && this.api.shouldShowBanner()) {
          return true;
        }
      } catch (error) {
        /* on continue avec les autres critères */
      }

      try {
        if (typeof this.api?.currentVisitorConsent === 'function') {
          const consent = this.api.currentVisitorConsent() || {};
          const decided = ['analytics', 'marketing', 'preferences'].some(
            (key) => consent[key] === 'yes' || consent[key] === 'no'
          );
          if (decided) return false;
        }
      } catch (error) {
        /* API incomplète : on affiche */
      }

      return true;
    }

    reveal() {
      /*
        Affichage synchrone : l'état visible ne doit dépendre d'aucun timer ni
        de requestAnimationFrame (onglet en arrière-plan, navigateur headless…).
        On force un reflow entre le retrait de `hidden` et le changement d'état
        pour que la transition CSS d'apparition joue quand même.
      */
      this.hidden = false;
      void this.offsetHeight;
      this.dataset.state = 'visible';
    }

    dismiss() {
      this.dataset.state = 'hidden';
      this.dataset.prefs = 'closed';
      window.setTimeout(() => {
        this.hidden = true;
      }, 350);
    }

    handleClick = (event) => {
      const trigger = event.target.closest('[data-fh-cookie-action]');
      if (!trigger || !this.contains(trigger)) return;

      switch (trigger.dataset.fhCookieAction) {
        case 'accept':
          this.setConsent({ analytics: true, marketing: true, preferences: true });
          break;
        case 'decline':
          this.setConsent({ analytics: false, marketing: false, preferences: false });
          break;
        case 'customize':
          this.togglePreferences();
          break;
        case 'save':
          this.setConsent({
            analytics: this.isChecked('analytics'),
            marketing: this.isChecked('marketing'),
            preferences: this.isChecked('preferences'),
          });
          break;
      }
    };

    isChecked(category) {
      return Boolean(this.toggles.find((input) => input.dataset.fhCookieConsent === category)?.checked);
    }

    togglePreferences() {
      const open = this.dataset.prefs !== 'open';
      this.dataset.prefs = open ? 'open' : 'closed';
      this.customizeButton?.setAttribute('aria-expanded', String(open));
    }

    setConsent({ analytics, marketing, preferences }) {
      /*
        Dans l'éditeur de thème, on ne mémorise rien : l'aperçu tourne sur le
        même domaine que la boutique, un clic « Tout accepter » y cacherait
        ensuite le bandeau sur le vrai site pour ce navigateur.
      */
      if (this.dataset.designMode === 'true') {
        this.dismiss();
        return;
      }

      this.rememberConsent();

      try {
        this.api?.setTrackingConsent(
          {
            analytics,
            marketing,
            preferences,
            sale_of_data: marketing,
          },
          () => {}
        );
      } catch (error) {
        /* le choix reste mémorisé localement */
      }

      this.dismiss();
    }
  }

  customElements.define('fh-cookie-bar', FhCookieBar);
})();
